import React from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminTable, { Column } from '../../components/AdminTable';
import { AdminUserDto, fetchAdminUsers } from '@/features/adminPlatform/api';

const PAGE_SIZE = 20;

const AdminUsersPage: React.FC = () => {
  const [rows, setRows] = React.useState<AdminUserDto[]>([]);
  const [page, setPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAdminUsers(page, PAGE_SIZE, query || undefined)
      .then(res => {
        if (cancelled) return;
        setRows(res.content);
        setTotalPages(res.totalPages);
      })
      .catch(err => !cancelled && setError(err?.message ?? 'Chyba načítání'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [page, query]);

  const columns: Column<AdminUserDto>[] = [
    { key: 'name', header: 'Jméno', render: r => `${r.firstName} ${r.lastName}` },
    { key: 'email', header: 'E-mail', render: r => r.email },
    { key: 'salon', header: 'Salon', render: r => r.salonName ?? <span className="text-gray-400 italic">—</span> },
    {
      key: 'role',
      header: 'Role',
      render: r =>
        r.isAdmin ? (
          <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">Admin</span>
        ) : (
          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">Uživatel</span>
        ),
    },
    {
      key: 'status',
      header: 'Stav',
      render: r => (
        <span
          className={`px-2 py-0.5 rounded text-xs ${
            r.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {r.status || '-'}
        </span>
      ),
    },
    { key: 'created', header: 'Vytvořen', render: r => new Date(r.createdAt).toLocaleDateString('cs-CZ') },
  ];

  return (
    <AdminLayout title="Uživatelé">
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <input
            type="search"
            value={query}
            onChange={e => {
              setPage(0);
              setQuery(e.target.value);
            }}
            placeholder="Hledat podle jména nebo e-mailu…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <AdminTable
          rows={rows}
          columns={columns}
          rowKey={r => r.id}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
